import { RoutesPath } from 'core/routes.types';
import { WorkflowStateContext, WorkflowStateEvent } from 'hooks/useWorkflowStateMachine/types';
import { useCallback, useMemo } from 'react';
import { generatePath, NavigateOptions, useNavigate, useSearchParams } from 'react-router-dom';
import { getCurrentConsumptionModeType, getRestaurantId } from 'state/app';
import { useAppSelector } from 'state/store';
import { convertModeToUrl } from 'utils/consumptionModes';
import * as Sentry from '@sentry/react';

export type CustomNavigateFunctions = keyof ReturnType<typeof useCustomNavigate>;

const PARAMS_TO_KEEP = ['tableId'];

export const useCustomNavigate = () => {
    const navigate = useNavigate();
    const restaurantId = useAppSelector(getRestaurantId);
    const consumptionMode = convertModeToUrl(useAppSelector(getCurrentConsumptionModeType));
    const [searchParams] = useSearchParams();

    const navigateWithParams = useCallback(
        (path: string, options: NavigateOptions = {}): void => {
            // Filter the parameters we want to retain
            const paramsToKeep: Record<string, string> = {};
            PARAMS_TO_KEEP.forEach((param) => {
                const value = searchParams.get(param);
                if (value) {
                    paramsToKeep[param] = value;
                }
            });

            // Build the new URL with the retained parameters
            const url = new URL(path, window.location.origin);
            Object.entries(paramsToKeep).forEach(([key, value]) => {
                url.searchParams.set(key, value);
            });

            if (Object.keys(options).length > 0) {
                navigate(`${path}${url.search}`, options);
            } else {
                navigate(`${path}${url.search}`);
            }
        },
        [navigate, searchParams],
    );

    /**
     * HOME
     */
    const navigateToHome = useCallback(
        () => navigateWithParams(RoutesPath.Home),
        [navigateWithParams],
    );
    const navigateToRestaurants = useCallback(() => {
        if (consumptionMode) {
            navigateWithParams(
                generatePath(RoutesPath.Restaurants, {
                    mode: consumptionMode,
                }),
            );
        } else {
            Sentry.captureMessage(`[navigateToRestaurants] consumptionMode is undefined`, 'debug');
            navigateToHome();
        }
    }, [consumptionMode, navigateWithParams, navigateToHome]);

    /**
     * MENU
     */
    const navigateToMenuHome = useCallback(() => {
        if (restaurantId && consumptionMode) {
            navigateWithParams(
                generatePath(RoutesPath.MenuHome, {
                    restaurantId: String(restaurantId),
                    mode: consumptionMode,
                }),
            );
        } else {
            Sentry.captureMessage(
                `[navigateToMenuHome] restaurantId (${restaurantId}) or consumptionMode (${consumptionMode}) is undefined`,
                'debug',
            );
            navigateToHome();
        }
    }, [restaurantId, consumptionMode, navigateWithParams, navigateToHome]);
    const navigateBackToMenuHome = useCallback(() => {
        restaurantId &&
            navigateWithParams(
                `../${generatePath(RoutesPath.MenuHome, {
                    restaurantId: String(restaurantId),
                    mode: consumptionMode,
                })}`,
            );
    }, [restaurantId, consumptionMode, navigateWithParams]);

    /**
     * LOGIN
     */
    const navigateToLogin = useCallback(() => {
        navigateWithParams(RoutesPath.Login);
    }, [navigateWithParams]);
    const navigateBackToLogin = useCallback(() => {
        navigateWithParams(`../${RoutesPath.Login}`);
    }, [navigateWithParams]);
    const navigateToForgottenPassword = useCallback(
        (email?: string) => {
            navigateWithParams(`../${RoutesPath.ForgottenPassword}`, {
                state: { email },
            });
        },
        [navigateWithParams],
    );

    /**
     * ACCOUNT
     */
    const navigateToAccount = useCallback(() => {
        navigateWithParams(`../${RoutesPath.Account}`);
    }, [navigateWithParams]);
    const navigateToOrdersHistory = useCallback(() => {
        navigateWithParams(`../${RoutesPath.OrdersHistory}`);
    }, [navigateWithParams]);

    /**
     * PRODUCT
     */
    const navigateToProduct = useCallback(
        (id: number) => navigateWithParams(`products/${id}`),
        [navigateWithParams],
    );

    /**
     * CROSS SELLING
     */
    const navigateToCrossSelling = useCallback(
        () => navigateWithParams(`cross-selling`),
        [navigateWithParams],
    );

    /**
     * CART
     */
    const navigateToCart = useCallback(
        () => navigateWithParams(RoutesPath.Cart),
        [navigateWithParams],
    );

    /**
     * CUSTOMER INFORMATION
     */
    const navigateToAdditionalInformations = useCallback(
        () => navigateWithParams(`${RoutesPath.AdditionalInformations}`),
        [navigateWithParams],
    );

    /**
     * PAYMENT
     */
    const navigateToChoosePayment = useCallback(
        () => navigateWithParams(RoutesPath.ChoosePayment),
        [navigateWithParams],
    );
    const navigateBackToChoosePayment = useCallback(
        () => navigateWithParams(`../${RoutesPath.ChoosePayment}`),
        [navigateWithParams],
    );
    const navigateToCardPayment = useCallback(() => {
        navigateWithParams(`../${RoutesPath.CardPayment}`);
    }, [navigateWithParams]);
    const navigateToGooglePayment = useCallback(() => {
        navigateWithParams(`../${RoutesPath.GooglePay}`);
    }, [navigateWithParams]);
    const navigateToApplePayment = useCallback(() => {
        navigateWithParams(`../${RoutesPath.ApplePay}`);
    }, [navigateWithParams]);
    const navigateToEdenredPayment = useCallback(() => {
        navigateWithParams(`../${RoutesPath.Edenred}`);
    }, [navigateWithParams]);
    const navigateToPaygreenPayment = useCallback(() => {
        navigateWithParams(`../${RoutesPath.Paygreen}`);
    }, [navigateWithParams]);
    const navigateToLuncheonVoucherPayment = useCallback(() => {
        navigateWithParams(`../${RoutesPath.LuncheonVoucherChoice}`);
    }, [navigateWithParams]);
    const navigateToPaymentLater = useCallback(() => {
        navigateWithParams(`../${RoutesPath.PaymentLaterChoice}`);
    }, [navigateWithParams]);
    const navigateToPaymentWithError = useCallback(
        (errorType: 'threeDS' | 'googleOrApplePay') =>
            navigateWithParams(`../${RoutesPath.ChoosePayment}`, {
                state:
                    errorType === 'googleOrApplePay'
                        ? { googlePayError: true, applePayError: true }
                        : { threeDSError: true },
            }),
        [navigateWithParams],
    );

    /**
     * GENERIC ERRORS
     */
    const navigateToNetworkError = useCallback(
        () => navigateWithParams(`../${RoutesPath.NetworkError}`),
        [navigateWithParams],
    );
    const navigateToTimeError = useCallback(
        () => navigateWithParams(`../${RoutesPath.TimeError}`),
        [navigateWithParams],
    );

    /**
     * ORDER
     */
    const navigateToOrderError = useCallback(
        (message?: string) =>
            navigateWithParams(`../${RoutesPath.OrderError}`, { state: { message } }),
        [navigateWithParams],
    );

    const navigateToCartError = useCallback(
        () => navigateWithParams(`../${RoutesPath.CartError}`),
        [navigateWithParams],
    );

    const navigateToMenuNotActivatedError = useCallback(
        () => navigateWithParams(`../${RoutesPath.MenuNotActivatedError}`),
        [navigateWithParams],
    );

    const navigateToOrderConfirmation = useCallback(
        (_context: WorkflowStateContext, event: WorkflowStateEvent) => {
            event.currentOrderId &&
                navigateWithParams(
                    generatePath(RoutesPath.OrderConfirmation, {
                        orderUuid: event.currentOrderId,
                    }),
                );
        },
        [navigateWithParams],
    );
    const navigateToOrderFollowUp = useCallback(
        (_context: WorkflowStateContext, event: WorkflowStateEvent) => {
            event.currentOrderId &&
                navigateWithParams(
                    generatePath(RoutesPath.OrderFollowUp, {
                        orderUuid: event.currentOrderId,
                    }),
                );
        },
        [navigateWithParams],
    );

    /**
     * TERMS AND CONDITIONS
     */
    const navigateToTermsAndConditions = useCallback(() => {
        navigateWithParams(`../${RoutesPath.TermsAndConditions}`);
    }, [navigateWithParams]);

    const navigateToStateMapper = useMemo(
        () => ({
            home: navigateToHome,
            login: navigateToLogin,
            restaurants: navigateToRestaurants,
            menuHome: navigateToMenuHome,
            cart: navigateToCart,
            crossSelling: navigateToCrossSelling,
            additionalInformations: navigateToAdditionalInformations,
            payment: navigateToChoosePayment,
            orderConfirmation: navigateToOrderConfirmation,
            orderFollowUp: navigateToOrderFollowUp,
            notFound: () => navigateWithParams(RoutesPath.NotFound),
        }),
        [
            navigateToHome,
            navigateToLogin,
            navigateToRestaurants,
            navigateToMenuHome,
            navigateToCart,
            navigateToCrossSelling,
            navigateToAdditionalInformations,
            navigateToChoosePayment,
            navigateToOrderConfirmation,
            navigateToOrderFollowUp,
            navigateWithParams,
        ],
    );

    const navigateToState = useCallback(
        (context: WorkflowStateContext, event: WorkflowStateEvent) => {
            return (
                event.redirectToState &&
                navigateToStateMapper[event.redirectToState](context, event)
            );
        },
        [navigateToStateMapper],
    );

    const handleGoBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    return {
        navigateWithParams,
        navigateToState,
        navigateToHome,
        navigateToRestaurants,
        navigateToMenuHome,
        navigateToLogin,
        navigateBackToLogin,
        navigateToForgottenPassword,
        navigateToAccount,
        navigateToOrdersHistory,
        navigateBackToMenuHome,
        navigateToProduct,
        navigateToCart,
        navigateToCrossSelling,
        navigateToAdditionalInformations,
        navigateToChoosePayment,
        navigateBackToChoosePayment,
        navigateToCardPayment,
        navigateToGooglePayment,
        navigateToApplePayment,
        navigateToEdenredPayment,
        navigateToPaygreenPayment,
        navigateToLuncheonVoucherPayment,
        navigateToPaymentLater,
        navigateToPaymentWithError,
        navigateToOrderError,
        navigateToTimeError,
        navigateToNetworkError,
        navigateToCartError,
        navigateToOrderConfirmation,
        navigateToOrderFollowUp,
        navigateToMenuNotActivatedError,
        navigateToTermsAndConditions,
        handleGoBack,
    };
};
